import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { Dispatch } from 'redux';
import { DocumentType, ReceiptAlert } from '@synergy/models';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import { hasErrors } from '@synergy/utils';

import { Api } from '../../../../api';
import { AppState } from '../../../../store/models/app-state';
import { CompleteInvoiceRequest } from '../models/invoice-request';
import { SkippedInvoiceResponse } from '../../invoice-api';
import { INVOICE_OVERVIEW_LABELS, INVOICE_POSITION_LABELS } from './invoice-position-labels';
import { paloMissingFieldsMessage } from '../../../../common/components/palo/palo-missing-fields-message';
import { paloReveal, paloRevealFirstError } from '../../../../common/utils/palo-reveal';
import { saveOverview } from './overview/invoice-detail-overview-actions';
import { savePositions } from './positions/invoice-detail-positions-actions';

export const INVOICE_DETAIL_RESET = 'INVOICE_DETAIL_RESET';
export const INVOICE_DETAIL_SAVE_ALL = 'INVOICE_DETAIL_SAVE_ALL';
export const INVOICE_DETAIL_COMPLETE = 'INVOICE_DETAIL_COMPLETE';
export const INVOICE_DETAIL_CANCELED = 'INVOICE_DETAIL_CANCELED';
export const INVOICE_DETAIL_IS_CREDIT_CHANGED = 'INVOICE_DETAIL_IS_CREDIT_CHANGED';
export const INVOICE_DETAIL_RECIPIENT_TAX_RATE_ID_CHANGED = 'INVOICE_DETAIL_RECIPIENT_TAX_RATE_ID_CHANGED';
export const INVOICE_DETAIL_UPDATE_ALERT = 'INVOICE_DETAIL_UPDATE_ALERT';
export const INVOICE_DETAIL_DELETE_ALERT = 'INVOICE_DETAIL_DELETE_ALERT';
/** A save the mask refused: the parts that are missing may now show it. */
export const INVOICE_DETAIL_SAVE_REFUSED = 'INVOICE_DETAIL_SAVE_REFUSED';

export const toggleIsCredit = () => ({ type: INVOICE_DETAIL_IS_CREDIT_CHANGED });

export const changeRecipientTaxRateId = (recipientTaxRateId: number | null) => ({
    type: INVOICE_DETAIL_RECIPIENT_TAX_RATE_ID_CHANGED,
    recipientTaxRateId,
});

export const Reset = () => ({
    type: INVOICE_DETAIL_RESET,
});

export const saveAll = (positionDetails: { netShippingCosts: number; netSurcharge: number; isTaxFree: boolean }) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const state = getState().invoiceDetail;

        dispatch({ type: INVOICE_DETAIL_SAVE_ALL });

        if (hasErrors(state.overview.stepData.errors) || hasErrors(state.positions.stepData.errors)) {
            dispatch({ type: INVOICE_DETAIL_SAVE_REFUSED });

            const headIncomplete = hasErrors(state.overview.stepData.errors);
            const withoutPositions = 0 === (state.positions.stepData.values.allItems?.length ?? 0);

            // Without a position the mask reads as complete — every field is
            // filled — so "check the form" sent people looking for a field
            // that is not there (#2206, R-134). Said on its own only while the
            // head is in order; otherwise the head is named too and goes
            // first, because its fields stand above the table (R-135).
            if (!headIncomplete && withoutPositions) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.invoices.detail.saveNeedsPosition')));
                paloReveal('invoice-positions', 'invoice-position-tools');

                return false;
            }

            const missing = paloMissingFieldsMessage([
                {
                    tab: 'app.invoices.detail.step.overview',
                    errors: state.overview.stepData.errors,
                    labels: INVOICE_OVERVIEW_LABELS,
                },
                {
                    tab: 'app.header.positions',
                    errors: state.positions.stepData.errors,
                    labels: INVOICE_POSITION_LABELS,
                },
            ]);

            dispatch(
                Snackbar.enqueue(
                    missing
                        ? PoolNotification.error('app.snackbar.formErrorsNamed', { fields: missing })
                        : PoolNotification.error('app.snackbar.validationErrors')
                )
            );

            // The head stands above the table, so it leads: the view goes to
            // its first marked field. Only a head in order sends it down.
            if (headIncomplete) {
                paloRevealFirstError();
            } else {
                paloReveal('invoice-positions');
            }

            return false;
        }

        // A document that has never been written cannot be "nothing to save",
        // whatever the form reports: without this the head was skipped, no
        // invoice was created, and the positions then had nothing to hang on
        // (#2210, R-02).
        const isNew = !state.overview.stepData.values.id;

        if (isNew || state.overview.stepData.isDirty) {
            if (!(await saveOverview()(dispatch, getState, { api }))) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.invoiceSavedError')));
                return false;
            }
        }

        // Costs and the tax-free flag are edited in the summary panel, outside the
        // positions form, so a change there alone must also trigger the save.
        const loaded = state.positions.stepData.values;
        const costsChanged =
            Number(loaded.netShippingCosts) !== positionDetails.netShippingCosts ||
            Number(loaded.netSurcharge) !== positionDetails.netSurcharge ||
            Boolean(loaded.isTaxFree) !== positionDetails.isTaxFree;

        if (state.positions.stepData.isDirty || costsChanged) {
            if (!(await savePositions(positionDetails)(dispatch, getState, { api }))) {
                dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.invoiceSavedError')));
                return false;
            }
        }

        // Saying "saved" after doing nothing is how a mask that saved nothing
        // still looked like it had (#2210, R-15).
        if (!isNew && !state.overview.stepData.isDirty && !state.positions.stepData.isDirty) {
            dispatch(Snackbar.enqueue(PoolNotification.info('app.snackbar.noChanges')));

            return true;
        }

        dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.saved')));
        return true;
    };
};

/**
 * Completes the open invoice.
 *
 * Answers with the entry the server skipped, or undefined when the invoice was
 * completed — so the mask can show the reason where it cannot be missed.
 */
export const complete = (params?: CompleteInvoiceRequest) => {
    return async (
        dispatch: Dispatch,
        getState: () => AppState,
        { api }: { api: Api }
    ): Promise<SkippedInvoiceResponse | undefined> => {
        const id = getState().invoiceDetail.overview.stepData.values.id;

        if (!id) {
            return undefined;
        }

        const response = await api.invoices.complete([id], params);

        if (!response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.invoiceSavedError')));

            return undefined;
        }

        const completed = response.result.completed[0];

        // The endpoint answers with what it completed and what it left alone
        // (#2136). A refused invoice used to arrive here as an empty list and
        // was read as a success with an undefined number.
        //
        // The reason goes back to the caller rather than into a snackbar that
        // is gone four seconds later: a refusal is the one thing on this mask
        // worth standing still for, and the batch says it in a dialog that has
        // to be dismissed (#2210, R-116).
        if (!completed) {
            return response.result.skipped[0] ?? { id, number: null, reason: 'notFound' };
        }

        dispatch({ type: INVOICE_DETAIL_COMPLETE, number: completed.number });

        return undefined;
    };
};

export const cancel = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const id = getState().invoiceDetail.overview.stepData.values.id;

        if (!id) {
            return false;
        }

        const result = await api.invoices.cancel(id);

        if (result.success) {
            dispatch({ type: INVOICE_DETAIL_CANCELED });
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.invoiceSavedError')));
            return false;
        }

        return true;
    };
};

export const updateReceiptAlert = (alert: ReceiptAlert) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const id = getState().invoiceDetail.overview.stepData.values.id;
        if (!id) {
            return;
        }

        const isNew = !getState().invoiceDetail.common?.receiptAlert;
        dispatch({ type: INVOICE_DETAIL_UPDATE_ALERT, alert });

        const response = await api.receiptAlert.updateAlert(DocumentType.Invoice, id, alert);
        if (response.success) {
            dispatch(
                Snackbar.enqueue(
                    PoolNotification.success(
                        isNew ? 'components.receiptAlert.snackbar.added' : 'components.receiptAlert.snackbar.changed'
                    )
                )
            );
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('components.receiptAlert.snackbar.error')));
        }
    };
};

export const deleteReceiptAlert = () => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        const id = getState().invoiceDetail.overview.stepData.values.id;
        if (!id) {
            return;
        }
        dispatch({ type: INVOICE_DETAIL_DELETE_ALERT });

        const response = await api.receiptAlert.deleteAlert(DocumentType.Invoice, id);
        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('components.receiptAlert.snackbar.deleted')));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('components.receiptAlert.snackbar.error')));
        }
    };
};
